/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2024 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 09/03/2024
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the partner form
 */
class Utf8EnabledToolProfileForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            ltiToolProfileId: null,
            ltiToolProfileCode: '',
            name: '',


            isDisplayLoader: true,

            errors: {},
            // user token
            token: '',
            // form fields
            fields: [
                {
                    inputType: 'dropdown',
                    labelName: 'Tool Code',
                    inputName: 'ltiToolProfileId',
                    optionValueName: 'ltiToolProfileId',
                    optionLabelName: 'ltiToolProfileCode',
                    required: true,
                    path: '',
                    valueType: 'utf8EnabledToolProfile'
                }
            ]
        };

        EventEmitter.emit('setPageHeaderData', {
            page: 'utf8EnabledToolProfile',
            pageName: "UTF8 Tool Profile",
            pageLink: process.env.REACT_APP_BASE_URL + "/#/home/utf8EnabledToolProfile",
            optionalIcons: ['backIcon']
        });

        // set event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
    }

    // set a token to unsubscribe the async call
    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {
        // get parameter from URL
        const utfToolProfileId = this.props.match.params.id;
        console.log("Utf8EnabledToolProfileForm");
        console.log(utfToolProfileId);

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create partner
        if (utfToolProfileId == undefined) {
            const cancelToken = axios.CancelToken;
            const src = cancelToken.source();

            const url = '/utf8EnabledToolProfile/allToolProfiles?page-no=0';

            await API.get(url, {headers: {Authorization: token}}).then(res => {
                const data = res.data[0];
                oldState.ltiToolProfileId = (data.utfToolProfileId !== undefined) ? data.utfToolProfileId : null;
                oldState.ltiToolProfileCode = (data.ltiToolProfileCode !== undefined) ? data.ltiToolProfileCode : '';

                oldState.token = token;
                oldState.isDisplayLoader = false;

                this.setState(oldState);
            }).catch(er => {
                oldState.isDisplayLoader = false;
                this.setState(oldState);
                this.displayErrorsForAPICalls("Something went wrong!");
            });

            src.cancel();
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all configured event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = null;

        data = {
            ltiToolProfileId: this.state.ltiToolProfileId,
            name: this.state.name,
            ltiToolProfileCode: this.state.ltiToolProfileCode
        };

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.ltiToolProfileId !== null) {
                this.enableEncoding(data);
            }
        }
    };

    enableEncoding = async (data) => {
        this.setState({isDisplayLoader: true});
        await API.post('/utf8EnabledToolProfile',
            {
                "toolProfileId": data.ltiToolProfileId,
                "name": data.name,
                "toolCode": data.ltiToolProfileCode
            }, {headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(res => {
            this.setState({isDisplayLoader: false});
            this.props.history.push('/home/utf8EnabledToolProfile');
            this.setAlert("Success", "Success", "Successfully Enabled the UTF8 Encoding!");
        }).catch(er => {
            this.setState({isDisplayLoader: false});
            if (er.response && er.response.status === 422) {
                this.displayErrorsForAPICalls("Already enabled the UTF8 encoding for this tool code!");
            } else {
                this.displayErrorsForAPICalls("Something went wrong!");
            }
        });
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'ltiToolProfileId':
                if (this.state.ltiToolProfileId === null)
                    error[key] = "Please select a Tool Code!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    displayErrorsForAPICalls = (error) => {
        this.setAlert("Error", "Error", error);
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/utf8EnabledToolProfile');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Enable UTF8 Encoding for this Tool Code" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default Utf8EnabledToolProfileForm;
